import { useCallback, useMemo } from 'react';
import {
  useNavigate as useRouterNavigate,
  useParams,
  To,
  NavigateOptions,
  NavigateFunction,
  useLocation,
} from 'react-router';
import { getBaseSubPath } from 'utils';
import {
  Cobrand,
  CobrandIdentifier,
  CobrandMap,
  isCobrand,
  normalizeCobrandPathParam,
} from '../types';
import { getCobrandFromHostName } from '../utils/cobrandUtils';

/**
 * Custom hook for handling cobrand-related functionality
 * @returns An object containing:
 *  - appCobrand: The raw cobrand value from URL parameters
 *  - cobrand: A validated cobrand value, defaulting to Cobrand.WELLFRAME if invalid
 */
export const useCobrand = () => {
  // Pull the raw cobrand path from the URL
  const { cobrand: rawPathParam } = useParams<{ cobrand?: string | undefined }>();
  const isCareElevateHost = window.location.hostname.toLowerCase().includes('careelevate');
  const shouldUseHostCareElevate = isCareElevateHost;
  const cobrandFromHost = getCobrandFromHostName();
  const shouldUseHostCobrand = !!cobrandFromHost;

  // Taking the raw cobrand path and converting it to a Cobrand type
  const cobrand = useMemo<Cobrand>(() => {
    if (shouldUseHostCareElevate) {
      return CobrandMap[CobrandIdentifier.CARE_ELEVATE];
    }
    if (cobrandFromHost) {
      return CobrandMap[cobrandFromHost];
    }

    const cobrandId = normalizeCobrandPathParam(rawPathParam) ?? CobrandIdentifier.WELLFRAME;
    return CobrandMap[cobrandId];
  }, [rawPathParam, shouldUseHostCareElevate, cobrandFromHost]);

  return { rawPathParam, cobrand, shouldUseHostCareElevate, shouldUseHostCobrand };
};

/**
 * A custom hook that wraps React Router's navigate function to handle cobrand-specific routing.
 * It prepends the valid cobrand name to the path if required.
 *
 * @returns A function that takes a path string and performs navigation
 *          while handling cobrand-specific path modifications.
 * @example
 * const navigate = useNavigate();
 * navigate('/home'); // For cobrand 'hphc' this will navigate to '/hphc/home'
 */
export const useNavigate = (): NavigateFunction => {
  const navigate = useRouterNavigate();
  const { cobrand, shouldUseHostCobrand, shouldUseHostCareElevate } = useCobrand();

  const cobrandNavigate = useCallback(
    (to: To | number, options?: NavigateOptions) => {
      if (typeof to === 'number') {
        navigate(to);
      }
      if (
        typeof to === 'string' &&
        to.startsWith('/') &&
        cobrand.id !== CobrandIdentifier.WELLFRAME &&
        !shouldUseHostCobrand &&
        !shouldUseHostCareElevate
      ) {
        navigate(`/${cobrand.id}${to}`, options);
        return;
      }
      navigate(to as To, options);
    },
    [navigate, cobrand, shouldUseHostCobrand, shouldUseHostCareElevate],
  );

  return cobrandNavigate;
};

export const getCobrandedPathWithSubpath = (
  cobrand: Cobrand,
  shouldUseHostCareElevate?: boolean,
  shouldUseHostCobrand?: boolean,
) => {
  const baseSubPath = getBaseSubPath();
  if (
    cobrand.id === CobrandIdentifier.WELLFRAME ||
    shouldUseHostCareElevate ||
    shouldUseHostCobrand
  ) {
    return baseSubPath;
  }
  return `${baseSubPath}/${cobrand.id}`;
};

/**
 * Utility function to get the base path for a cobrand.
 *
 * @param cobrand The cobrand object
 * @returns The path including the cobrand identifier
 */
const getCobrandedBasePath = (
  cobrand: Cobrand,
  shouldUseHostCareElevate: boolean,
  shouldUseHostCobrand: boolean,
) => {
  // Don't include baseSubPath - React Router's basename prop handles /member-web
  if (
    cobrand.id === CobrandIdentifier.WELLFRAME ||
    shouldUseHostCareElevate ||
    shouldUseHostCobrand
  ) {
    return '';
  }
  return `/${cobrand.id}`;
};

/**
 * Custom hook to get the path for a specific route.
 *
 * @returns The base path including the cobrand identifier
 */
export const useCobrandedPath = (path: string) => {
  const { cobrand, shouldUseHostCareElevate, shouldUseHostCobrand } = useCobrand();
  return getCobrandedBasePath(cobrand, shouldUseHostCareElevate, shouldUseHostCobrand) + path;
};

export const useCobrandedPathWithSubpath = (path: string) => {
  const { cobrand, shouldUseHostCareElevate, shouldUseHostCobrand } = useCobrand();
  return (
    getCobrandedPathWithSubpath(cobrand, shouldUseHostCareElevate, shouldUseHostCobrand) + path
  );
};

/**
 * Custom hook to get the uncobranded path for the current route.
 *
 * @returns The path without the cobrand identifier
 */
export const useUncobrandedPathName = (): string => {
  const { pathname } = useLocation();
  const firstPathSegment = pathname.split('/')[1];

  if (isCobrand(firstPathSegment)) {
    const uncobrandedPath = pathname.replace(new RegExp(`^/${firstPathSegment}`), '');
    return uncobrandedPath || '/';
  }

  return pathname;
};

/** * Custom hook to reset query parameters in the current URL.
 * This is useful for clearing any query parameters that may have been set during navigation.
 *
 * @returns A function that resets the query parameters by navigating to the current pathname
 */
export const useResetQueryParameters = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  return () => navigate(pathname, { replace: true });
};

export const getPlayStoreLink = (cobrand: Cobrand) =>
  `https://play.google.com/store/apps/details?id=${cobrand.androidPackageName}`;

export const getAppStoreLink = (cobrand: Cobrand) =>
  `https://apps.apple.com/app/id${cobrand.iosAppStoreID}`;
